/**
 * Hook that evaluates surveyForm visibility rules against current form values
 * and returns the set of field IDs that should be visible.
 *
 * Supports:
 * - SimpleEqualityQuestionVisibility: field value === expected value
 * - LogicalQuestionVisibility (Or): any child visibility satisfied
 * - Chained visibility: if a parent is hidden, dependents are also hidden
 * - Dropdown (Option): single selected value compared directly
 * - Checkboxes (MultipleOptions): comma-separated values, any match counts
 */

import * as React from 'react';
import {
	FilterOperator,
	LogicalQuestionVisibility,
	QuestionVisibility,
	SimpleEqualityQuestionVisibility,
	SurveyForm,
} from '../../../../../extViewmodels';
import { IPublicFileAttachment } from '../../../../models/Api';

type FormValues = Record<string, string | IPublicFileAttachment[]>;

/**
 * Checks whether a single visibility condition is satisfied given current form values.
 */
const isVisibilitySatisfied = (
	visibility: QuestionVisibility,
	formValues: FormValues,
	visibleFieldIds: Set<string>,
	depth: number
): boolean => {
	// Guard against depth recursion
	if (depth > 20) return false;

	if (visibility._type === 'SimpleEqualityQuestionVisibility') {
		const simple = visibility as SimpleEqualityQuestionVisibility;

		// If the field we depend on is itself not visible, condition is not met
		if (!visibleFieldIds.has(simple.fieldId)) return false;

		const currentValue = formValues[simple.fieldId];
		if (typeof currentValue !== 'string') return false;

		// For checkboxes (MultipleOptions), value is comma-separated IDs
		const selectedValues = currentValue.split(',').filter(v => v.trim() !== '');
		return selectedValues.includes(simple.fieldValue);
	}

	if (visibility._type === 'LogicalQuestionVisibility') {
		const logical = visibility as LogicalQuestionVisibility;

		if (logical.logicalOperator === FilterOperator.Or) {
			return logical.visibilities.some(v => isVisibilitySatisfied(v, formValues, visibleFieldIds, depth + 1));
		}

		if (logical.logicalOperator === FilterOperator.And) {
			return logical.visibilities.every(v => isVisibilitySatisfied(v, formValues, visibleFieldIds, depth + 1));
		}
	}

	return false;
};

/**
 * Computes the set of visible field IDs based on surveyForm rules and current values.
 *
 * Fields without a visibleWhen rule are always visible.
 * Fields with visibleWhen are visible only when their condition is satisfied.
 * Evaluation respects ordering — a field can only depend on fields earlier in the list.
 */
export const computeVisibleFields = (surveyForm: SurveyForm | undefined, formValues: FormValues): Set<string> => {
	const visibleFieldIds = new Set<string>();

	if (!surveyForm?.surveyParts?.length) {
		return visibleFieldIds; // Empty set — caller should treat as "show all"
	}

	for (const part of surveyForm.surveyParts) {
		const question = part as { fieldId?: string; visibleWhen?: QuestionVisibility | null };
		if (!question.fieldId) continue;

		if (!question.visibleWhen) {
			// No condition — always visible
			visibleFieldIds.add(question.fieldId);
		} else if (isVisibilitySatisfied(question.visibleWhen, formValues, visibleFieldIds, 0)) {
			visibleFieldIds.add(question.fieldId);
		}
		// Otherwise: not visible (not added to set)
	}

	return visibleFieldIds;
};

/**
 * React hook that returns the set of visible field IDs.
 *
 * @param surveyForm - The survey's form layout with visibility rules
 * @param customProperties - Current form values keyed by field ID
 * @returns Set of field IDs that should be visible. If surveyForm has no parts,
 *          returns undefined (meaning no filtering — show all fields).
 */
export const useConditionalVisibility = (
	surveyForm: SurveyForm | undefined,
	customProperties: FormValues
): Set<string> | undefined => {
	return React.useMemo(() => {
		if (!surveyForm?.surveyParts?.length) {
			return undefined; // No surveyForm — show all fields
		}
		return computeVisibleFields(surveyForm, customProperties);
	}, [surveyForm, customProperties]);
};
